<template>
  <BaseModalEditCreate
    :mode="mode == 'edit' && props.item?.createdDate ? 'edit' : 'add'"
    :title="modalTitle()"
    :is-modified="isModified"
    :disable="mode == 'view'"
    :close-on-outside-click="false"
    max-width="narrow"
    @dismiss-changes="emit('close')"
    @save="onSave">
    <template #form>
      <div class="space-y-2">
        <div>
          <label class="block text-sm font-medium leading-6 text-gray-900">{{
            $t('discountMatrix.materialGroup')
          }}</label>
          <BaseSelectDropdown
            :items="dropdownMaterialGroups"
            :selected-item="selectedMaterialGroup"
            flat
            clearable
            :mode="mode"
            size="sm"
            name="action"
            show-error-as-tooltip
            :error="getInputErrors('materialGroup')"
            @update:selected="handleMaterialGroupSelection" />
        </div>
        <div>
          <label class="block text-sm font-medium leading-6 text-gray-900">{{
            $t('discountMatrix.traderSiteGroup')
          }}</label>
          <BaseSelectDropdown
            :items="dropdownTraderSiteGroups"
            :selected-item="selectedTraderSiteGroup"
            flat
            clearable
            :mode="mode"
            size="sm"
            name="action"
            show-error-as-tooltip
            :error="getInputErrors('traderSiteGroup')"
            @update:selected="handleTraderSiteGroupSelection">
          </BaseSelectDropdown>
        </div>
        <div>
          <label class="block text-sm font-medium leading-6 text-gray-900">{{
            $t('material.material')
          }}</label>
          <div v-if="mode == 'view'">{{ modifiedData.material?.name || '-' }}</div>
          <BaseSearchDropdown
            v-else
            v-model="selectedMaterial"
            :list-items="dropdownMaterials"
            class=""
            size="sm"
            :noResultsText="$t('messages.noResultsText')"
            :count="materialCount"
            show-error-as-tooltip
            :error="getInputErrors('material')"
            @clear-selection="handleMaterialSelection"
            @updateSearch="updateMaterialsSearch"
            @item-selected="handleMaterialSelection"></BaseSearchDropdown>
        </div>
        <div>
          <label class="block text-sm font-medium leading-6 text-gray-900">{{
            $t('discountMatrix.traderSite')
          }}</label>
          <div v-if="mode == 'view'">{{ modifiedData.traderSite?.name || '-' }}</div>
          <BaseSearchDropdown
            v-else
            v-model="selectedTradersite"
            :list-items="dropdownTraderSites"
            size="sm"
            :noResultsText="$t('messages.noResultsText')"
            :count="tradersiteCount"
            show-error-as-tooltip
            :error="getInputErrors('traderSite')"
            @clear-selection="handleTradersiteSelection"
            @updateSearch="updateTradersitesSearch"
            @item-selected="handleTradersiteSelection"></BaseSearchDropdown>
        </div>
        <div>
          <label for="sequence" class="text-sm font-medium">{{ $t('baseObject.sequence') }}</label>
          <BaseInput
            v-model="modifiedData.discountSequence"
            color="base"
            class="h-8"
            type="number"
            show-error-as-tooltip
            :error="getInputErrors('sequence')" />
        </div>
        <div class="flex gap-3">
          <div>
            <label for="discount" class="text-sm font-medium">{{
              $t('discountMatrix.discount')
            }}</label>
            <BaseInput
              v-model="discountPC"
              type="number"
              color="base"
              class="h-8"
              show-error-as-tooltip
              :error="getInputErrors('discountPC')"
              @input="onValueInputChange(0)" />
          </div>
          <div>
            <label for="value" class="text-sm font-medium">{{ $t('discountMatrix.value') }}</label>
            <BaseInput
              v-model="discountValue"
              type="number"
              color="base"
              class="h-8"
              show-error-as-tooltip
              :error="getInputErrors('discountValue')"
              @input="onValueInputChange(1)" />
          </div>
        </div>
        <BaseToggle
          label="Active"
          name="detailIsActive"
          v-model="modifiedData.isActive"
          class="pb-10" />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf, helpers, required } from '@vuelidate/validators';

import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseToggle from '../common/BaseToggle.vue';

import type { PricingPolicyMatrixDetail } from '@/types/PricingTypes';
import { getMaterialsWithConditions } from '@/services/api/materials';
import { getTraderSitesWithConditions } from '@/services/api/traders';
import {
  getGroupItemsMaterialsGroupItems,
  getGroupItemsTraderSitesGroupItems,
} from '@/services/api/groups';
import type { SelectDropdownItem } from '@/types/Base';

import { useI18n } from 'vue-i18n';
import { deepCopy } from '@/utils/data';

const props = defineProps<{
  mode: 'view' | 'edit';
  item: PricingPolicyMatrixDetail;
}>();

const { t: $t } = useI18n({ useScope: 'global' });

const modifiedData = ref<PricingPolicyMatrixDetail>(
  deepCopy<PricingPolicyMatrixDetail>(props.item)
);
const isModified = ref<boolean>(false);

const emit = defineEmits(['close', 'save']);

const discountPC = ref();
const discountValue = ref();

const dropdownMaterialGroups = ref<SelectDropdownItem[]>([]);
const selectedMaterialGroup = ref<SelectDropdownItem | null>(null);

function handleMaterialGroupSelection(item: SelectDropdownItem) {
  selectedMaterialGroup.value = item;
  if (modifiedData.value) {
    modifiedData.value.materialGroupId = item ? (item.value as string) : undefined;
    if (item)
      modifiedData.value.materialGroup = {
        id: String(item.value),
        name: item.name,
      };
    else modifiedData.value.materialGroup = undefined;
  }
}

const dropdownTraderSiteGroups = ref<SelectDropdownItem[]>([]);
const selectedTraderSiteGroup = ref<SelectDropdownItem | null>(null);

function handleTraderSiteGroupSelection(item: SelectDropdownItem) {
  selectedTraderSiteGroup.value = item;
  if (modifiedData.value) {
    modifiedData.value.traderSiteGroupId = item ? (item.value as string) : undefined;
    if (item)
      modifiedData.value.traderSiteGroup = {
        id: String(item.value),
        name: item.name,
      };
    else modifiedData.value.traderSiteGroup = undefined;
  }
}

const dropdownMaterials = ref<SelectDropdownItem[]>([]);
const selectedMaterial = ref<SelectDropdownItem | null>(null);
const materialCount = ref(0);

async function updateMaterialsSearch(searchInput: string, page?: number) {
  const items = await getMaterialsWithConditions({
    page: page,
    itemsPerPage: 20,
    query: searchInput,
    sortColumn: 'name',
    sortAscending: true,
    includeDeleted: false,
    grouped: true,
  });
  if (items.count > 0 && items.data) {
    if (page && page > 1 && dropdownMaterials.value)
      dropdownMaterials.value = dropdownMaterials.value.concat(
        items.data.map((t) => ({
          value: t.id,
          name: t.name,
        })) as SelectDropdownItem[]
      );
    else
      dropdownMaterials.value = items.data.map((t) => ({
        value: t.id || '',
        name: t.name,
      }));
  } else dropdownMaterials.value = [];
  materialCount.value = items.count;
}

function handleMaterialSelection(item: SelectDropdownItem) {
  selectedMaterial.value = item;

  modifiedData.value.materialId = item ? (item.value as string) : undefined;
  if (item)
    modifiedData.value.material = {
      id: String(item.value),
      name: item.name,
    };
  else modifiedData.value.material = undefined;
}

const dropdownTraderSites = ref<SelectDropdownItem[]>([]);
const selectedTradersite = ref<SelectDropdownItem | null>(null);
const tradersiteCount = ref(0);

async function updateTradersitesSearch(searchInput: string, page?: number, id?: string) {
  const items = await getTraderSitesWithConditions({
    page: page,
    itemsPerPage: 20,
    query: searchInput,
    sortColumn: 'name',
    sortAscending: true,
    grouped: true,
    id: id,
    isActive: true,
  });
  if (items.count > 0 && items.data) {
    if (page && page > 1 && dropdownTraderSites.value)
      dropdownTraderSites.value = dropdownTraderSites.value.concat(
        items.data.map((t) => ({
          value: t.id,
          name: `${t.name}, ${t.address}`,
        })) as SelectDropdownItem[]
      );
    else
      dropdownTraderSites.value = items.data.map((t) => ({
        value: t.id || '',
        name: `${t?.name || '-'}${t?.address ? `, ${t.address}${t.city ? `, ${t.city}` : ''}${t.zipCode ? `, ${t.zipCode}` : ''}` : ''}`,
      }));
  } else dropdownTraderSites.value = [];
  tradersiteCount.value = items.count;
}

function handleTradersiteSelection(item: SelectDropdownItem) {
  selectedTradersite.value = item;

  modifiedData.value.traderSiteId = item ? (item.value as string) : undefined;
  if (item)
    modifiedData.value.traderSite = {
      id: String(item.value),
      name: item.name,
    };
  else modifiedData.value.traderSite = undefined;
}

function onValueInputChange(discountType: 0 | 1) {
  discountType == 0
    ? ((modifiedData.value.value = discountPC.value), (discountValue.value = undefined))
    : ((modifiedData.value.value = discountValue.value), (discountPC.value = undefined));
  modifiedData.value.discountType = discountType;
}

onMounted(async () => {
  modifiedData.value.discountType == 0
    ? (discountPC.value = modifiedData.value.value || null)
    : (discountValue.value = modifiedData.value.value || null);
  await initDropdownData();
  await initDropdowns();
});

async function initDropdownData() {
  const materialGroups = await getGroupItemsMaterialsGroupItems();
  if (materialGroups) {
    dropdownMaterialGroups.value = materialGroups.data.map((x) => ({
      value: x.id,
      name: x.name,
    }));
  }

  const traderSiteGroups = await getGroupItemsTraderSitesGroupItems();
  if (traderSiteGroups) {
    dropdownTraderSiteGroups.value = traderSiteGroups.data.map((x) => ({
      value: x.id,
      name: x.name,
    }));
  }
}

async function initDropdowns() {
  if (!props.item || props.mode == 'view') return;
  if (props.item.materialId != null && props.item.material && props.item.material.name) {
    await updateMaterialsSearch(props.item.material.name, undefined);
    const materialValue = dropdownMaterials.value.find(
      (item) => item.value == props.item?.materialId
    );
    if (materialValue) selectedMaterial.value = materialValue;
  } else await updateMaterialsSearch('', 1);

  if (props.item.traderSiteId != null && props.item.traderSite) {
    await updateTradersitesSearch('', undefined, props.item?.traderSiteId);
    const traderSiteValue = dropdownTraderSites.value.find(
      (item) => item.value == props.item?.traderSiteId
    );
    if (traderSiteValue) selectedTradersite.value = traderSiteValue;
  } else await updateTradersitesSearch('', 1);

  if (props.item.materialGroupId != null) {
    const materialGroup = dropdownMaterialGroups.value.find(
      (item) => item.value == props.item?.materialGroupId
    );
    if (materialGroup) selectedMaterialGroup.value = materialGroup;
  }
  if (props.item.traderSiteGroupId != null) {
    const traderSiteGroup = dropdownTraderSiteGroups.value.find(
      (item) => item.value == props.item?.traderSiteGroupId
    );
    if (traderSiteGroup) selectedTraderSiteGroup.value = traderSiteGroup;
  }
}
watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.material = newData.materialId || undefined;
    validationModel.value.materialGroup = newData.materialGroupId || undefined;
    validationModel.value.traderSite = newData.traderSiteId || undefined;
    validationModel.value.traderSiteGroup = newData.traderSiteGroupId || undefined;
    validationModel.value.discountPC = newData.discountType == 0 ? newData.value : undefined;
    validationModel.value.discountValue = newData.discountType == 1 ? newData.value : undefined;
    validationModel.value.sequence = newData.discountSequence || 1;

    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.item);
    isModified.value = valueChanged || $v.value.$invalid;
  },
  { deep: true }
);

const validationModel = ref({
  material: modifiedData.value.materialId || undefined,
  materialGroup: modifiedData.value.materialGroupId || undefined,
  traderSite: modifiedData.value.traderSiteId || undefined,
  traderSiteGroup: modifiedData.value.traderSiteGroupId || undefined,
  discountPC: modifiedData.value.discountType == 0 ? modifiedData.value.value : undefined,
  discountValue: modifiedData.value.discountType == 1 ? modifiedData.value.value : undefined,
  sequence: modifiedData.value.discountSequence || 1,
});

const traderSiteValidator = computed(() => {
  return !!modifiedData.value.traderSiteGroupId && !!modifiedData.value.traderSiteId;
});

const materialValidator = computed(() => {
  return !!modifiedData.value.materialGroupId && !!modifiedData.value.materialId;
});

const rules = computed(() => ({
  material: {
    notBoth: helpers.withMessage(
      'Both Material group and Material cannot be filled at the same time. One of them should be.',
      () => !materialValidator.value
    ),
    requiredIf: helpers.withMessage(
      `${$t('messages.errors.required')}`,
      requiredIf(function () {
        return !modifiedData.value.materialGroupId;
      })
    ),
  },
  materialGroup: {
    notBoth: helpers.withMessage(
      'Both Material group and Material cannot be filled at the same time. One of them should be.',
      () => !materialValidator.value
    ),
    requiredIf: helpers.withMessage(
      `${$t('messages.errors.required')}`,
      requiredIf(function () {
        return !modifiedData.value.materialId;
      })
    ),
  },
  traderSite: {
    notBoth: helpers.withMessage(
      'Both Trader site group and Trader site cannot be filled at the same time. One of them should be.',
      () => !traderSiteValidator.value
    ),
    requiredIf: helpers.withMessage(
      `${$t('messages.errors.required')}`,
      requiredIf(function () {
        return !modifiedData.value.traderSiteGroupId;
      })
    ),
  },
  traderSiteGroup: {
    notBoth: helpers.withMessage(
      'Both Trader site group and Trader site cannot be filled at the same time. One of them should be.',
      () => !traderSiteValidator.value
    ),
    requiredIf: helpers.withMessage(
      `${$t('messages.errors.required')}`,
      requiredIf(function () {
        return !modifiedData.value.traderSiteId;
      })
    ),
  },
  discountPC: {
    requiredIf: helpers.withMessage(`${$t('messages.errors.required')}`, (value: any) => {
      if (modifiedData.value.discountType == 0 && !value) return false;
      return true;
    }),
    minValue: helpers.withMessage(`${$t('messages.errors.invalidValue')}`, (value: any) => {
      if (modifiedData.value.discountType == 0 && !value) return true;
      return modifiedData.value.discountType == 0 ? !isNaN(value) && value > 0 : true;
    }),
  },
  discountValue: {
    requiredIf: helpers.withMessage(`${$t('messages.errors.required')}`, (value: any) => {
      if (modifiedData.value.discountType == 1 && !value) return false;
      return true;
    }),
    minValue: helpers.withMessage(`${$t('messages.errors.invalidValue')}`, (value: any) => {
      return modifiedData.value.discountType == 1 ? !isNaN(value) && value > 0 : true;
    }),
  },
  sequence: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    minValue: helpers.withMessage(`${$t('messages.errors.invalidValue')}`, (value: any) => {
      const numberValue = Number(value);
      return Number.isInteger(numberValue) && numberValue > 0;
    }),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function getInputErrors(
  type:
    | 'material'
    | 'materialGroup'
    | 'traderSite'
    | 'traderSiteGroup'
    | 'discountPC'
    | 'discountValue'
    | 'sequence'
) {
  if ($v.value[type].required && $v.value[type].required.$invalid)
    return $v.value[type].required.$message;
  else if ($v.value[type].requiredIf && $v.value[type].requiredIf.$invalid)
    return $v.value[type].requiredIf.$message;
  else if ($v.value[type].minValue && $v.value[type].minValue.$invalid)
    return $v.value[type].minValue.$message;
  else if ($v.value[type].notBoth && $v.value[type].notBoth.$invalid)
    return $v.value[type].notBoth.$message;
  else return '';
}

function modalTitle() {
  if (props.mode == 'view' || !modifiedData.value.isFromManager)
    return `${$t('common.actions.view')}  item `;
  return props.mode === 'edit' && props.item?.createdDate
    ? `${$t('common.actions.edit')} item`
    : `${$t('common.actions.create')} item`;
}

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  modifiedData.value.discountType === 0
    ? (modifiedData.value.discountTypeToken = '%')
    : (modifiedData.value.discountTypeToken = '€');
  modifiedData.value.modified = isModified.value;
  emit('save', modifiedData.value);
}
</script>
